import { useEffect, useState } from "react";

import styles from "styles/pages/reports/reports.module.scss";

import * as echarts from "echarts";
import { Button, Drawer } from "@components";

import { IdentificationGroupChart } from "pages/Reports/Customers/Identification/IdentificationGroupChart";
import { Numbers } from "shared/utils/Numbers";

const groups = (data) => {
  return [
    {
      alias: "COMPLETAMENTE_IDENTIFICADOS",
      name: "Completamente identificados",
      borderColor: "#fff",
      valueColor: "rgba(51, 51, 51, 1)",
      bgColor: "#2a2a2a",
      total: data?.complete?.value || 0,
      percentage: data?.complete?.percentage || 0,
    },
    {
      alias: "APENAS_TELEFONE",
      name: "Apenas Telefone",
      borderColor: "#F1B6C6",
      valueColor: "rgba(241, 182, 198, 0.15)",
      bgColor: "#292627",
      total: data?.phone?.value || 0,
      percentage: data?.phone?.percentage || 0,
    },
    {
      alias: "APENAS_CPF",
      name: "Apenas CPF",
      borderColor: "#55E092",
      valueColor: "rgba(85, 224, 146, 0.15)",
      bgColor: "#212824",
      total: data?.document?.value || 0,
      percentage: data?.document?.percentage || 0,
    },
    {
      alias: "APENAS_EMAIL",
      name: "Apenas E-mail",
      borderColor: "#FABF55",
      valueColor: "rgba(250, 191, 85, 0.15)",
      bgColor: "#2a2721",
      total: data?.email?.value || 0,
      percentage: data?.email?.percentage || 0,
    },
    {
      alias: "TELEFONE_CPF",
      name: "Telefone + CPF",
      borderColor: "#05AFF2",
      valueColor: "rgba(5, 175, 242, 0.15)",
      bgColor: "#1d2629",
      total: data?.phone_document?.value || 0,
      percentage: data?.phone_document?.percentage || 0,
    },
    {
      alias: "TELEFONE_EMAIL",
      name: "Telefone + E-mail",
      borderColor: "#B219FF",
      valueColor: "rgba(178, 25, 255, 0.15)",
      bgColor: "#261e2a",
      total: data?.phone_email?.value || 0,
      percentage: data?.phone_email?.percentage || 0,
    },
    {
      alias: "CPF_EMAIL",
      name: "CPF + E-mail",
      borderColor: "#8682FA",
      valueColor: "rgba(134, 130, 250, 0.15)",
      bgColor: "#24242a",
      total: data?.document_email?.value || 0,
      percentage: data?.document_email?.percentage || 0,
    },
  ];
};

export default function IdentificationGroup({ data, loading, onFetchHistory }) {
  const [selectedData, setSelectedData] = useState(null);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const chartDOM = document.getElementById("customer-chart");
      const chart = echarts.init(chartDOM, null, {
        renderer: "svg",
      });

      const getData = () => {
        const _data = groups(data).map((category) => ({
          value: category.total,
          name: category.name,
          itemStyle: {
            color: category.borderColor || "#fff",
          },
        }));

        return _data;
      };

      const options = {
        tooltip: {
          trigger: "item",
          responsive: true,
          formatter: (param) => {
            const data = param.data;
            return [
              `<span>${
                data.name
              }</span><br/><span style="opacity: 50%">${data.value.toLocaleString(
                "pt-BR"
              )} clientes </span>`,
            ].join("");
          },
          backgroundColor: "#242424",
          borderWidth: "0",
          borderColor: "transparent",
          textStyle: {
            color: "#fff",
            fontSize: 10,
          },
        },
        series: [
          {
            type: "pie",
            radius: "95%",
            label: {
              show: false,
              position: "center",
            },
            data: getData(),
          },
        ],
      };

      options && chart.setOption(options);

      const ro = new ResizeObserver(() => {
        chart.resize();
      });
      ro.observe(chartDOM);

      return () => {
        ro.unobserve(chartDOM);
        ro.disconnect();
        chart.dispose();
      };
    }
  }, []);

  return (
    <div className={styles.box}>
      <div className="d-flex align-center justify-between mb-20">
        <div className="d-flex align-center gap-5">
          <p className={styles.label}>Grupo de identificação</p>
          <span
            className={styles.info}
            title="Quantidade de clientes por combinação de dados identificados."
          >
            <i className="fal fa-info-circle" />
          </span>
        </div>
        <Button
          design="default-icon-mini-3"
          onClick={() =>
            setSelectedData({
              name: "Grupo de identificação",
            })
          }
        >
          <i className="fas fa-chart-waterfall" />
        </Button>
      </div>

      <div className={styles.container}>
        <div className={styles.customers}>
          <div className={styles.grid}>
            {groups(data).map((group) => {
              return (
                <div className={styles.category} key={group.alias}>
                  <div
                    className={styles.border}
                    style={{
                      backgroundColor: group.borderColor || "#fff",
                    }}
                  />
                  <div className="d-flex align-center">
                    <p
                      className={styles.percent}
                      style={{ backgroundColor: group.valueColor || "#fff" }}
                    >
                      {Numbers.number(group.percentage)}%
                    </p>
                    <p className={styles.name}>{group.name}</p>
                  </div>
                  <div
                    className={styles.percentage}
                    style={{
                      width: `${group.percentage}%`,
                      backgroundColor: group.bgColor || "#fff",
                    }}
                  />
                  <p className={styles.value}>
                    {group.total.toLocaleString("pt-BR")} clientes
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        <div className={styles.chart}>
          <div
            id="customer-chart"
            style={{ width: "100%", height: "100%", minHeight: 300 }}
          />
        </div>
      </div>

      <Drawer
        visible={selectedData}
        onClose={() => setSelectedData(null)}
        withoutTransition
        direction="full-center"
        showCloseButton={false}
      >
        <IdentificationGroupChart
          selectedData={selectedData}
          onFetchData={onFetchHistory}
          loading={loading}
          onClose={() => setSelectedData(false)}
          startOn2025
          startOnMonth={6}
          startOnYear={2025}
        />
      </Drawer>
    </div>
  );
}
